import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { visuallyHidden } from "@mui/utils";
import { authContext } from "../Context/Authentication/authContext";
import Map from "./Components/Map/Map";
import "./Predictions.scss";
import { baseURL, dataScienceURL } from "../config";
import { predictionTableHeaders, excelData } from "../Helpers/helpers";
import DownloadExcelButton from "./DownloadExcelButton";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Bars } from "react-loader-spinner";
import { width } from "@amcharts/amcharts4/.internal/core/utils/Utils";

const round = (num: number) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface FilterState {
  column: string;
  operator: string;
  value: string;
}

interface HeaderCell {
  name: string;
  key: string;
  disablePadding?: boolean;
  interval?: string;
}

interface EnhancedTableProps {
  order: 'asc' | 'desc';
  orderBy: string;
  headers: HeaderCell[];
  interval: any;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  onSearchChange: (value: string) => void;
  onApplyFilter: (column: string, operator: string, value: string) => void;
  clear:boolean
}
const formComponentStyles = {
  select: {
    backgroundColor: "#333",
    color: "white",
    borderRadius: 1,
    height: "45px",
    '& .MuiSelect-icon': {
      color: 'white',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'white !important',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'white !important',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'white !important',
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      width:"120px",
       
      '& fieldset': {
        width:"120px",
        borderColor: 'white !important',
      },
      '&:hover fieldset': {
        borderColor: 'white !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'white !important',
      },
    },
    '& .MuiInputBase-input': {
      color: 'white',
      height: '20px',
    },
    '& .MuiInputLabel-root': {
      color: 'white',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: 'white',
    },
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'white !important',
      },
      '&:hover fieldset': {
        borderColor: 'white !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'white !important',
      },
    },
    '& .MuiInputBase-input': {
      color: 'white',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: 'white',
    },
  },
};
const defaultOperator = "=";

export function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, headers, interval, onSearchChange, onApplyFilter,clear } = props;

  // State for managing multiple filter states
  const [filterStates, setFilterStates] = useState<Record<string, FilterState>>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<string>("");
  const [searchIconSelected, setSearchIconSelected] = useState<Record<string, boolean>>({});
  const [filterIconSelected, setFilterIconSelected] = useState<Record<string, boolean>>({});

  // Initialize filter states for each header
  useEffect(() => {
    const initialFilterStates: Record<string, FilterState> = {};
    headers.forEach((header) => {
      if (!filterStates[header.key]) {
        initialFilterStates[header.key] = {
          column: header.key,
          operator: defaultOperator,
          value: ""
        };
      }
    });
    setFilterStates(prev => ({ ...prev, ...initialFilterStates }));
  }, [headers]);
  
  useEffect(()=>{
    if(clear){
      setFilterIconSelected((prevFilters) =>
        Object.keys(prevFilters).reduce((acc:any, key) => {
          acc[key] = false;
          return acc;
        }, {})
      );
    }
  },[clear])
  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  const handleSearchIconClick = (event: React.MouseEvent<HTMLElement>, column: string) => {
    setAnchorEl(event.currentTarget);
    setActiveColumn(column);
    setSearchIconSelected(prev => ({ ...prev, [column]: true }));
  };

  const handleFilterIconClick = (event: React.MouseEvent<HTMLElement>, column: string) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveColumn(column);
    setFilterIconSelected(prev => ({ ...prev, [column]: true }));
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchIconSelected(prev => ({ ...prev, [activeColumn]: false }));
  };

  const handleFilterClose = () => {
    // Reset filterIconSelected for the active column if no filter was applied
    setFilterIconSelected(prev => ({
      ...prev,
      [activeColumn]: filterStates[activeColumn]?.value ? true : false, // Retain color only if value exists
    }));
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (column: string, field: string, value: string) => {
    setFilterStates(prev => ({
      ...prev,
      [column]: {
        ...prev[column],
        [field]: value
      }
    }));
  };

  const handleApplyFilter = (column: string) => {
    const filterState = filterStates[column];
    if (filterState.value) {
      onApplyFilter(filterState.column, filterState.operator, filterState.value || "0");
      setFilterIconSelected(prev => ({
        ...prev,
        [column]: true, // Change color to indicate filter is applied
      }));
    }
    handleFilterClose();
  };

  return (
    <TableHead>
      <TableRow>
        {headers.map((headCell, index) => {
          if (headCell.interval && headCell.interval !== interval) return null;
          const isSearchable = headCell.key === "city" || headCell.key === "zipCode";
          const currentFilter = filterStates[headCell.key];
          return (
            <TableCell
              key={headCell.key}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.key ? order : false}
              align="center"
              sx={{
                position: "sticky",
                left: 0,
                zIndex: index === 0 ? 999 : 1,
                "&:hover .search-icon, &:hover .filter-icon": {
                  color: "black",
                  display: "block"
                }
              }}
            >
              <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px"
              }}>
                <TableSortLabel
                  active={orderBy === headCell.key}
                  direction={orderBy === headCell.key ? order : "asc"}
                  onClick={createSortHandler(headCell.key)}
                >
                  {headCell.name}
                </TableSortLabel>

                {isSearchable ? (
                  <>
                    <IconButton
                      onClick={(e) => handleSearchIconClick(e, headCell.key)}
                      size="small"
                      sx={{ padding: 0 }}
                    >
                      <SearchIcon
                        className="search-icon"
                        sx={{
                          color: searchIconSelected[headCell.key] ? "#f7b047" : "white",
                          fontSize: "18px"
                        }}
                      />
                    </IconButton>
                    <Popover
                      open={Boolean(anchorEl) && activeColumn === headCell.key}
                      anchorEl={anchorEl}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                    >
                      <Box p={2}>
                        <TextField
                          placeholder={`Search ${headCell.name}`}
                          size="small"
                          onChange={(e) => onSearchChange(e.target.value)}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "white",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "white",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "white",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Box>
                    </Popover>
                  </>
                ) : (
                  <>
                    <IconButton
                      onClick={(e) => handleFilterIconClick(e, headCell.key)}
                      size="small"
                      sx={{ padding: 1 }}
                    >
                      <FilterListIcon
                        className="filter-icon"
                        sx={{
                          color: filterIconSelected[headCell.key] ? "#f7b047" : "white",
                          fontSize: "18px"
                        }}
                      />
                    </IconButton>
                    <Popover
                      open={Boolean(filterAnchorEl) && activeColumn === headCell.key}
                      anchorEl={filterAnchorEl}
                      onClose={handleFilterClose}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                    >
                      <Box p={2}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px"
                        }}>
                          <FormControl style={{ width: "100px" }}>
                            <InputLabel>Operator</InputLabel>
                            <Select
                              value={currentFilter?.operator || defaultOperator}
                              onChange={(e) => handleFilterChange(headCell.key, "operator", e.target.value)}
                              label="Operator"
                              sx={formComponentStyles.select}
                            >
                              {[">", "<", ">=", "<=", "="].map(op => (
                                <MenuItem key={op} value={op}>{op}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label="Value"
                            value={currentFilter?.value || ""}
                            onChange={(e) => handleFilterChange(headCell.key, "value", e.target.value)}
                            type="number"
                            sx={formComponentStyles.textField}
                          />
                        </div>
                        <Box sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: 1
                        }}>
                          <button
                            onClick={() => handleApplyFilter(headCell.key)}
                            style={{
                              background: "#f7b047",
                              padding: "8px",
                              borderRadius: "4px",
                              fontWeight: "bold",
                              cursor: "pointer"
                            }}
                          >
                            Apply Filter
                          </button>
                        </Box>
                      </Box>
                    </Popover>
                  </>
                )}
              </Box>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

const Predictions = () => {
  const [interval, setInterval] = useState(false);
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState("ppsf");
  const [geoData, setGeoData] = useState({});
  const { state }: any = useContext(authContext);
  const [predictionsData, setPredictionsData]: any = useState([]);
  const [headers, setHeaders]: any = React.useState([]);
  const [zipcode, setZipcode] = React.useState<string>("");
  const [isLoading, setLoading]: any = React.useState(true);
  const [clear, setClear]: any = React.useState(false);
  const [xlsData, setXlsData]: any = useState([]);
  const [xlsType, setXlsType]: any = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  useEffect(() => {
    if (state.filters.selectedType === "cityPredictions") {
      getCityPredictions();
    } else if (state?.filters?.selectedType === "zipcodePredictions") {
      getPredictionByZipCodeSummary();
    } 
    else if (state?.filters?.selectedType === "countyPredictions") {
      getCountyPredictions();
    }  
    else {
      getPredictionByZipCode();
    }
  }, [
    state.filters.predictionFor,
    state.filters.noOfPrediction,
    state.filters.selectedType,
    state.filters.quarterPredictionFor,
    state.filters.state,
    state.filters.city,
    state.filters.zipcodeQuarterPredictionFor,
  ]);

  useEffect(() => {
    setXlsData(
      excelData(headers, filteredPredictions(state.filters.selectedType))
    );
  }, [searchQuery, appliedFilters]);

  const handleIntervalChange = () => {
    setInterval(!interval);
  };
  const filteredPredictions = (type: string) => {
    let filteredData = predictionsData;
    Object.entries(appliedFilters).forEach(([column, columnFilters]: any) => {
      columnFilters.forEach((filter: any) => {
        filteredData = filteredData.filter((row: any) => {
          const cellValue = round(row[column]);
          const filterValue = parseFloat(filter.value);
          const operator = filter.operator;
          switch (operator) {
            case ">":
              return cellValue > filterValue;
            case "<":
              return cellValue < filterValue;
            case ">=":
              return cellValue >= filterValue;
            case "<=":
              return cellValue <= filterValue;
            case "=":
              return cellValue === filterValue;
            default:
              return true;
          }
        });
      });
    });
    if (searchQuery.trim() != "") {
      if (type === "cityPredictions") {
        return filteredData.filter((row: any) =>
          row.city?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      } else {
        return filteredData.filter((row: any) =>
          row.zipCode?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
    } else {
      return filteredData;
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };
  const handleApplyFilter = (column: string, operator: string, value: string) => {
    setClear(false)
    setAppliedFilters((prevFilters: any) => {
      // Check if the column already exists in the state
      if (prevFilters[column]) {
        return {
          ...prevFilters,
          [column]: prevFilters[column].map((filter: any, index: number) => {
            if (index === 0) {
              // Update the first entry for this column
              return { operator, value };
            }
            return filter;
          }),
        };
      } else {
        // If the column does not exist, add a new entry
        return {
          ...prevFilters,
          [column]: [{ operator, value }],
        };
      }
    });
  };
  
    

  const getCityPredictions = async () => {
    try {
      setLoading(true)
      const response = await axios.get(
        `${baseURL}getCityPredictions?state=${state.filters.state}&date=${state.filters.predictionFor}&limit=${state.filters.noOfPrediction}`
      );
      const tableHeaders = predictionTableHeaders("cityPredictions");
      setHeaders(tableHeaders);
      setPredictionsData(response.data?.cube);
      setXlsData(excelData(tableHeaders, response.data?.cube));
      setXlsType("city-prediction-summary");
      setLoading(false)
    } catch (error) {
      console.log("error", error);
      setLoading(false)
    }
  };

  const getCountyPredictions = async () => {
    try {
      setLoading(true)
      const response = await axios.get(
        `${baseURL}getCountyPredictionSummary?state=${state.filters.state}&date=${state.filters.predictionFor}&limit=${state.filters.noOfPrediction}`
      );
      const tableHeaders = predictionTableHeaders("countyPredictions");
      setHeaders(tableHeaders);
      setPredictionsData(response.data?.cube);
      setXlsData(excelData(tableHeaders, response.data?.cube));
      setXlsType("county-prediction-summary");
      setLoading(false)
    } catch (error) {
      console.log("error", error);
      setLoading(false)
    }
  };
  const getPredictionByZipCode = async () => {
    try {
      setLoading(true)
      const response = await axios.get(
        `${dataScienceURL}getZipCodePredictions?state=${state.filters.state}&city=${state.filters.city}&date=${state.filters.quarterPredictionFor}&limit=${state.filters?.noOfPrediction}`
      );
      const tableHeaders = predictionTableHeaders("zipcodePredictions");
      setHeaders(tableHeaders);
      setPredictionsData(response.data?.cube);
      setXlsData(excelData(tableHeaders, response.data?.cube));
      setXlsType("zipcode-prediction-summary");
      setLoading(false)
    } catch (error) {
      console.log("error", error);
      setLoading(false)
    }
  };

  const getPredictionByZipCodeSummary = async () => {
    try {
      setLoading(true)
      const response = await axios.get(
        `${baseURL}getZipCodePredictionsSummary?state=${state.filters.state}&date=${state.filters.zipcodeQuarterPredictionFor}&limit=${state.filters?.noOfPrediction}`
      );
      const tableHeaders = predictionTableHeaders("zipcodePredictions");
      setHeaders(tableHeaders);
      setPredictionsData(response.data?.cube);
      setXlsData(excelData(tableHeaders, response.data?.cube));
      setXlsType("zipcode-prediction-summary");
      setLoading(false)
    } catch (error) {
      console.log("error", error);
      setLoading(false)
    }
  };

  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleZipCodeClick = async (row: any) => {
    const key = state.filters.selectedTerritory.toLowerCase();
    let geoLocationInfo = await getGeoJSONInfo(key);
    setGeoData(geoLocationInfo);
    setZipcode(row.zipCode);
  };
  const getGeoJSONInfo = async (key: string) => {
    let resp;
    let data = {};
    resp = await fetch(`../fullgeoJsonData/${key}.json`);
    data = await resp.json();
    return data;
  };
  const handleClearFilters = () => {
    setSearchQuery("");
    setAppliedFilters({});
    setClear(true)
    if (state.filters.selectedType === "cityPredictions") {
      getCityPredictions();
    } else if (state?.filters?.selectedType === "zipcodePredictions") {
      getPredictionByZipCodeSummary();
    } else {
      getPredictionByZipCode();
    }
  };

  return (
    <div className="predictions-main">
      <div className="predictions-main__map-container">
      {isLoading ? (
          <Bars
            height="400"
            ariaLabel="bars-loading"
            wrapperClass="msa-spinner-progress-bar"
            visible={true}
          />
        ) : (
          <Map
          cityPredictions={predictionsData}
          stateCord={state.filters.stateCord}
          geoData={geoData || null}
          zipCode={zipcode}
        />
        )}
      </div>
      <div className="predictions-main__interval-check">
        <FormControlLabel
          label="Click here for interval estimates"
          control={
            <Checkbox checked={interval} onChange={handleIntervalChange} />
          }
        />
        <DownloadExcelButton jsonData={xlsData} type={xlsType} />
        {Object.keys(appliedFilters).length != 0 && (
          <Button
            className="excel-button"
            style={{ marginRight: "12px" }}
            // endIcon={<DownloadIcon/>}
            onClick={handleClearFilters}
            // disabled={type === ''}
          >
            Clear Filters
          </Button>
        )}
      </div>
      <div className="predictions-main__table-container">
        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
          <Table
            stickyHeader
            className="predictions-main__table-container__table"
            aria-label="simple table"
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headers={headers}
              interval={interval}
              onSearchChange={handleSearchChange}
              onApplyFilter={handleApplyFilter}
              clear={clear}
            />
            <TableBody>
              {stableSort(
                filteredPredictions(state?.filters?.selectedType),
                getComparator(order, orderBy)
              ).map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <TableRow
                    key={index}
                    className="predictions-main__table-container__row"
                  >
                    <TableCell
                      className="predictions-main__table-container__row__cell zipCell"
                      id={labelId}
                      component="th"
                      scope="row"
                      align="left"
                      // onClick={() => handleZipCodeClick(row)}
                      // style={{ cursor: "pointer" }}
                    >
                      {state.filters.selectedType === "cityPredictions"
                        ? row.city
                        : state.filters.selectedType === "countyPredictions"?row.county:row.zipCode }
                    </TableCell>

                    {interval && (
                      <TableCell align="center">{round(row.ppsfLow)}</TableCell>
                    )}
                    <TableCell align="center">{round(row.ppsf)}</TableCell>
                    {interval && (
                      <TableCell align="center">
                        {round(row.ppsfHigh)}
                      </TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">
                        {round(row.ppsfLowGrowth)}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {round(row.ppsfMeanGrowth)}
                    </TableCell>
                    {interval && (
                      <TableCell align="center">
                        {round(row.ppsfHighGrowth)}
                      </TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">{round(row.mspLow)}</TableCell>
                    )}
                    <TableCell align="center">{round(row.msp)}</TableCell>
                    {interval && (
                      <TableCell align="center">{round(row.mspHigh)}</TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">
                        {round(row.mspLowGrowth)}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {round(row.mspMeanGrowth)}
                    </TableCell>
                    {interval && (
                      <TableCell align="center">
                        {round(row.mspHighGrowth)}
                      </TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">{round(row.domLow)}</TableCell>
                    )}
                    <TableCell align="center">{round(row.domMean)}</TableCell>
                    {interval && (
                      <TableCell align="center">{round(row.domHigh)}</TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">
                        {round(row.homeSalesLow)}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {round(row.homeSalesMean)}
                    </TableCell>
                    {interval && (
                      <TableCell align="center">
                        {round(row.homeSalesHigh)}
                      </TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">
                        {row.home_sales_cumulative_low
                          ? round(row.home_sales_cumulative_low)
                          : "-"}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {row.home_sales_cumulative_mean
                        ? round(row.home_sales_cumulative_mean)
                        : "-"}
                    </TableCell>
                    {interval && (
                      <TableCell align="center">
                        {row.home_sales_cumulative_high
                          ? round(row.home_sales_cumulative_high)
                          : "-"}
                      </TableCell>
                    )}
                    {state.filters.selectedType === "cityPredictions" && (
                      <>
                        <TableCell align="center">
                          {row.population_2021
                            ? round(row.population_2021)
                            : "-"}
                        </TableCell>
                        <TableCell align="center">
                          {row.population_Growth_1_year
                            ? round(row.population_Growth_1_year)
                            : "-"}
                        </TableCell>
                        <TableCell align="center">
                          {row.population_growth_3_year
                            ? round(row.population_growth_3_year)
                            : "-"}
                        </TableCell>
                        <TableCell align="center">
                          {row.population_growth_5_year
                            ? round(row.population_growth_5_year)
                            : "-"}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {predictionsData?.length === 0 && (
            <div className="predictions-main__noData">No data available</div>
          )}
        </TableContainer>
      </div>
      <div className="predictions-main__notes-container">
        <div className="predictions-main__notes-container__heading">Notes</div>
        <ul>
          <li>DOM : Days on market</li>
          <li>HS : Home Sales</li>
          <li>HSC : Home sales cumulative</li>
        </ul>
      </div>
    </div>
  );
};

export default Predictions;